<template>
    <NuxtLink
        class="simple-link"
        :to="getInternalLink(blok.link)"
        :rel="blok.rel ? blok.rel.join(' ') : null"
        :target="target"
        :class="linkClasses"
        :style="linkStyles"
        external
    >
        <span v-html="blok.text"></span>
    </NuxtLink>
</template>

<script lang="ts" setup>
import type { ICoreSimpleLink } from '~ui/types/components/CoreSimpleLink';
import { ELinkTarget } from '~ui/types/enums/LinkAttributes';
import { buildFonts, buildTextStyles } from '~ui/utils/styleBuilder';
import { getInternalLink } from '~ui/utils/internalLinking';
import { EHoverEffect } from '~ui/types/enums/HoverEffect';
import { EAlignText, EUnderlineTypes } from '~ui/types/enums/ComponentStyle';

const { blok } = defineProps<{ blok: ICoreSimpleLink }>();

const target = computed(() => {
    return blok.link.target ? blok.link.target : ELinkTarget.SELF;
});

// styling from storyblok
const linkStyles = computed(() => {
    return {
        ...buildFonts(blok.font[0]),
        ...buildTextStyles(blok.font[0]),
    };
});

// hover classes for the link
const hoverClasses = computed(() => {
    return {
        'hover-underline': blok.hoverUnderline,
        'hover-primary': blok.hoverEffect === EHoverEffect.TEXTPRIMARY,
        'hover-secondary': blok.hoverEffect === EHoverEffect.TEXTSECONDARY,
        'hover-fade': blok.hoverEffect === EHoverEffect.FADE,
    };
});

const linkClasses = computed(() => {
    return {
        ...hoverClasses.value,
        // apply full width class if text align is not LEFT
        'w-full': blok.font?.[0]?.alignment && blok.font[0].alignment !== EAlignText.LEFT,
        // underline classes
        'has-divider': blok.font?.[0]?.underline === EUnderlineTypes.DIVIDER,
        'has-underline-border': blok.font?.[0]?.underline === EUnderlineTypes.BORDER,
    };
});
</script>

<style lang="postcss" scoped>
.simple-link {
    @apply inline-block transition-all duration-300 ease-in-out;

    &.has-underline-border {
        @apply border-b-[1px];
        border-color: var(--font-color, #000);
    }
    &.has-divider {
        @apply relative;
        &:before {
            @apply content-[""] h-[3px] w-full absolute bottom-px left-0;
            background-color: var(--font-color, #000);
        }
    }
}
.hover-underline {
    @apply hover:!underline;
}
.hover-primary {
    @apply hover:!text-primary;
}
.hover-secondary {
    @apply hover:!text-secondary;
}
.hover-fade {
    @apply hover:brightness-[90%];
}
</style>
